import { useCallback, useEffect, useRef, useState } from 'react'
import { runBulkJob, type BulkJob, type BulkJobResult } from '../bulkJob'

export type FanJobKind = 'add' | 'remove'

export interface FanCardState {
  state: 'running' | 'done' | 'failed'
  error?: string
}

export interface FanJobView {
  kind: FanJobKind
  listName: string
  total: number
  finished: number
}

interface Start {
  kind: FanJobKind
  listName: string
  fanIds: number[]
  /** One OnlyFans call for one fan. */
  run: (fanId: number) => Promise<void>
}

/**
 * The page's one bulk fan job (decision 15): paced OnlyFans calls with each
 * card's state, progress for the selection bar, Cancel, and what to retry.
 * Leaving the page stops it.
 */
export function useFanJob() {
  const [cards, setCards] = useState<Record<number, FanCardState>>({})
  const [job, setJob] = useState<FanJobView | null>(null)
  const current = useRef<BulkJob | null>(null)
  const last = useRef<Start | null>(null)

  useEffect(() => () => current.current?.cancel(), [])

  const start = useCallback(async (spec: Start): Promise<BulkJobResult> => {
    last.current = spec
    setJob({ kind: spec.kind, listName: spec.listName, total: spec.fanIds.length, finished: 0 })
    setCards((c) => {
      const next = { ...c }
      for (const id of spec.fanIds) delete next[id]
      return next
    })
    const bulk = runBulkJob({
      items: spec.fanIds.map(String),
      run: (id) => spec.run(Number(id)),
      onUpdate: (id, s) => {
        setCards((c) => ({ ...c, [id]: s.status === 'failed' ? { state: 'failed', error: s.error } : { state: s.status } }))
        if (s.status !== 'running') setJob((j) => (j ? { ...j, finished: j.finished + 1 } : j))
      },
    })
    current.current = bulk
    const result = await bulk.result
    if (current.current === bulk) {
      current.current = null
      setJob(null)
    }
    return result
  }, [])

  const cancel = useCallback(() => current.current?.cancel(), [])

  /** Forget card states (e.g. another list opened). */
  const reset = useCallback(() => setCards({}), [])

  const failedIds = Object.entries(cards)
    .filter(([, s]) => s.state === 'failed')
    .map(([id]) => Number(id))

  return { cards, job, start, cancel, reset, failedIds, lastRun: last }
}
